<template>
  <Section
    id="shipment-progress-section"
    :header="t('shipments.shipmentProgress')"
    class="shipment-progress-section"
  >
    <span v-if="shipmentTimelineItems.length === 0">
      {{ t('common.noParam', { param: t('orders.trackingInformation') }) }}
    </span>
    <Timeline
      v-else
      :value="shipmentTimelineItems"
      class="shipment-timeline bg-gray-lighter timeline-background"
    >
      <template #marker>
        <div class="timeline-event-marker-custom-wrapper">
          <span
            class="timeline-event-marker-custom relative flex align-items-center justify-content-center border-circle"
          >
          </span>
        </div>
      </template>
      <template #content="{ item, index }">
        <p
          v-if="item.actualDate"
          class="font-bold pb-1 flex flex-gap-1 align-items-center"
        >
          {{
            renderDateTime(item.actualDate, {
              timeFormat: 'NONE',
            })
          }}
          <Tag
            v-if="index === shipmentTimelineItems.length - 1"
            class="most-recent-label"
            :value="t('shipments.mostRecent')"
          />
        </p>
        <p v-if="item.actualDate" class="font-bold pb-1">
          {{
            renderDateTime(item.actualDate, {
              dateFormat: 'NONE',
            })
          }}
        </p>
        <span v-if="isShipmentEvent(item)">
          {{
            item.eventType
              ? translateEnum(item.eventType, 'ShipmentEventType')
              : '-'
          }}
        </span>
        <span v-if="isShipmentServiceIncident(item)">
          {{ item.incidentType }}
        </span>
        <span v-if="item.actualLocation?.city || item.actualLocation?.state">
          {{ ' -- ' }}
        </span>
        <span v-if="item.actualLocation?.city">
          {{ item.actualLocation.city + ' ' }}
        </span>
        <span v-if="item.actualLocation?.state">
          {{ item.actualLocation.state }}
        </span>
        <i
          v-if="isShipmentServiceIncident(item)"
          aria-hidden="true"
          class="pi pi-exclamation-triangle"
          style="color: #dd9642"
        />
      </template>
    </Timeline>
  </Section>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderDateTime, translateEnum } from '@rygen/rygen-ui/i18n';

import { useShipmentTimeLineItems } from '@/composables/useShipmentTimeLineItems';
import { ShipmentEvent, ShipmentServiceIncident, TimelineItem } from '@/model';
import { buildShipmentEvent } from '@/util';

import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Tag from 'primevue/tag';
import Timeline from 'primevue/timeline';

const props = defineProps({
  events: {
    type: Array as PropType<ShipmentEvent[]>,
    default: () => [],
  },
  serviceIncidents: {
    type: Array as PropType<ShipmentServiceIncident[]>,
    default: () => [],
  },
});
const { events, serviceIncidents } = toRefs(props);

const { t } = useI18n();
const shipmentTimeLineItemsUtil = useShipmentTimeLineItems();

const shipmentTimelineItems = computed(() => {
  return shipmentTimeLineItemsUtil.toShipmentTimeLineItems(
    [],
    events.value.filter((e) => !!e.actualDate).map(buildShipmentEvent),
    serviceIncidents.value
      .filter((e) => !!e.actualDate)
      .map((e) => new ShipmentServiceIncident(e)),
    [],
  );
});

const isShipmentEvent = (
  timelineItem: TimelineItem,
): timelineItem is ShipmentEvent => {
  return timelineItem instanceof ShipmentEvent;
};

const isShipmentServiceIncident = (
  timelineItem: TimelineItem,
): timelineItem is ShipmentServiceIncident => {
  return timelineItem instanceof ShipmentServiceIncident;
};
</script>

<style>
:root {
  --shipment-progress-timeline-gap: 2rem;
  --shipment-progress-marker-size: 1rem;
  --shipment-progress-marker-size-milestone: calc(
    var(--shipment-progress-marker-size) * 2
  );
  --shipment-progress-event-header-box-height: 43.5px;
}

.shipment-progress-section {
  --p-tag-primary-background: var(--success-color);
  --p-tag-primary-color: var(--success-light-color);

  .shipment-timeline {
    gap: var(--shipment-progress-timeline-gap);

    .p-timeline-event-opposite {
      display: none;
    }

    .p-timeline-event {
      min-height: 0px !important;
      display: flex;
      flex-flow: row nowrap;

      .p-timeline-event-separator {
        min-width: min(
          var(--shipment-progress-marker-size),
          var(--shipment-progress-marker-size-milestone)
        );
        margin-right: var(--sp-2);
      }

      .timeline-event-marker-custom-wrapper {
        height: var(--shipment-progress-event-header-box-height);
        display: flex;
        flex-flow: column;
        justify-content: flex-start;

        &.is-milestone {
          --shipment-progress-event-header-box-height: 65.7px;
        }

        & + .p-timeline-event-connector {
          background-color: var(--p-gray-200);
        }

        &.is-resolved + .p-timeline-event-connector {
          background-color: var(--success-color);
        }

        &.is-late + .p-timeline-event-connector {
          background-color: var(--warning-color);
        }
      }

      .timeline-event-marker-custom {
        height: var(--shipment-progress-marker-size);
        width: var(--shipment-progress-marker-size);
        color: var(--p-gray-200);
        background-color: var(--p-gray-200);
        box-sizing: content-box;
        border: calc(var(--shipment-progress-timeline-gap) / 4) solid
          var(--p-gray-50);
        z-index: 1;

        &.is-late {
          background-color: var(--warning-color);
        }
      }

      .p-timeline-event-connector {
        position: absolute;
        top: var(--shipment-progress-timeline-gap);
        bottom: calc(var(--shipment-progress-timeline-gap) * -4);
        width: 4px !important;
        z-index: 0;
      }
    }
  }

  .timeline-background {
    border-left: 3em solid var(--p-gray-50);
    border-top: 1em solid var(--p-gray-50);
    margin-top: 1em;
    border-bottom: 3em solid var(--p-gray-50);
  }
}
</style>
